/**
 * NfcStatusIndicator
 *
 * Top-bar status indicator for Shaka Bridge + reader + realtime
 * state. Visual chrome matches the redesign mockup
 * (`shaka-deck-mockup-shell.html` — "NFC indicator · 4 states"):
 * a 38px circular tile coloured by the active state, with a small
 * status-dot anchored to the bottom-right that mirrors the same
 * tone. The internal 6-state machine collapses to 4 visible kinds:
 *
 *   ok        ready                          (sage)
 *   scanning  busy / connecting              (sun, pulses)
 *   offline   degraded / reader_busy         (coral)
 *   idle      disconnected / no_reader       (sand)
 *
 * The connector logic (auto-connect on mount, hover-to-retry on
 * disconnected, download CTA in tooltip) is unchanged.
 */

import { useEffect, useMemo } from 'react';
import { Radio, WifiOff, Download, AlertCircle, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNfcStatus } from '@/shared/hooks/useNfcStatus';
import { nfcConnector } from '@/shared/services/nfcConnectorService';
import { deriveApiBaseUrl } from '@/shared/services/apiBase';
import { detectOS } from '@/shared/utils/detectOS';
import { useAuth } from '@/core/auth/useAuth';
import { useActiveContext } from '@/shared/hooks/useActiveContext';
import { canManagePOS, canManageGuests } from '@/core/auth/modulePermissions';
import { getAccessibleLocationIds } from '@/core/auth/permissions';
import { useTranslation } from '@/core';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/theme/components/ui/tooltip';
import trayIconMacUrl from '@/assets/shaka-bridge-tray-mac.png';
import trayIconWinUrl from '@/assets/shaka-bridge-tray-win.png';

/**
 * Public download URL for the shaka-bridge installer, per OS. Served by
 * the backend's download endpoint, which 302-redirects to the signed +
 * notarized installer for that platform. Built from the resolved API base
 * so it auto-targets staging/production with the deck build (no hardcoded
 * host). 'other' falls back to the macOS build (bridge ships mac + windows).
 */
function bridgeDownloadUrl(os: 'mac' | 'windows' | 'other'): string {
  const base = deriveApiBaseUrl().replace(/\/+$/, '');
  const platform = os === 'windows' ? 'windows' : 'macos';
  return `${base}/api/v1/updates/bridge/download/?platform=${platform}`;
}

type Visual = 'ok' | 'scanning' | 'offline' | 'idle';

interface VisualConfig {
  /** Outer 38px tile chrome: bg + border + icon color. */
  tile: string;
  /** Status-dot bottom-right colour. */
  dot: string;
  /** When true, render an animated ring around the tile. */
  pulse: boolean;
  /** lucide icon. */
  Icon: React.ComponentType<{ className?: string }>;
  iconClass?: string;
}

const VISUAL: Record<Visual, VisualConfig> = {
  ok: {
    tile: 'bg-sage-50 border-sage-300 text-sage-700',
    dot: 'bg-sage-500',
    pulse: false,
    Icon: Radio,
  },
  scanning: {
    tile: 'bg-sun-50 border-sun-300 text-sun-700',
    dot: 'bg-sun-500',
    pulse: true,
    Icon: Radio,
    iconClass: 'animate-pulse',
  },
  offline: {
    tile: 'bg-coral-50 border-coral-300 text-coral-700',
    dot: 'bg-coral-500',
    pulse: false,
    Icon: AlertCircle,
  },
  idle: {
    tile: 'bg-sand-100 border-sand-200 text-sand-700',
    dot: 'bg-sand-400',
    pulse: false,
    Icon: WifiOff,
  },
};

export function NfcStatusIndicator() {
  const state = useNfcStatus();
  const { t } = useTranslation();
  const os = useMemo(() => detectOS(), []);
  const { user } = useAuth();
  const { activeLocationId } = useActiveContext();

  // The connector session endpoint (POST /pos/nfc-connector/session/) is
  // guarded by CanUseNFCConnector on the backend, which accepts POS operators
  // (location.manage_pos) OR guest managers (location.manage_guests) — the NFC
  // reader is used for wristband check-in, a front-desk/guest operation, not
  // only for POS. A role with neither (HR / Treasury / …) can never obtain a
  // session, so booting a connect for them only produced a silent background
  // 403 and an NFC tile they cannot use; gate the whole indicator to mirror
  // the backend. In company scope (no active location) allow it when the user
  // qualifies at any accessible location, or holds company full access —
  // wristbands resolve tenant-wide, matching the header comment.
  const canUseNfc = useMemo(() => {
    const qualifies = (id?: string | null) => canManagePOS(user, id) || canManageGuests(user, id);
    if (qualifies(activeLocationId)) return true;
    if (!activeLocationId) {
      return getAccessibleLocationIds(user).some((id) => qualifies(id));
    }
    return false;
  }, [user, activeLocationId]);

  // One silent connect attempt per page load (app open / refresh) so the
  // indicator goes green without hovering when the bridge is running.
  // Single-shot by design: a user without the local bridge gets exactly
  // one browser-logged connection refusal per load (the browser logs it
  // natively, JS can't suppress it) — never a polling loop. Further
  // attempts remain user-triggered (hover/click below). Skipped entirely
  // for roles that can't hold a connector session (see canUseNfc).
  useEffect(() => {
    if (!canUseNfc) return;
    nfcConnector.tryConnectOnBoot();
  }, [canUseNfc]);

  const handleUserTrigger = () => {
    if (
      state === 'ready'
      || state === 'busy'
      || state === 'no_reader'
      || state === 'degraded'
      // Reconnecting does nothing here: the bridge is up, another application
      // is holding the reader. Only closing that application fixes it.
      || state === 'reader_busy'
    ) {
      return;
    }
    void nfcConnector.tryConnectFromUi();
  };

  // Map the 6-state internal machine onto the 4 visual variants.
  const visual: Visual =
    state === 'ready'
      ? 'ok'
      : state === 'busy' || state === 'connecting'
      ? 'scanning'
      : state === 'degraded' || state === 'reader_busy'
      ? 'offline'
      : 'idle';

  const cfg = VISUAL[visual];
  const Icon = visual === 'scanning' && state === 'connecting' ? Loader2 : cfg.Icon;

  // Tooltip text — keeps the per-state granularity even though the
  // visual collapses some states together.
  const labelMap: Record<typeof state, string> = {
    disconnected: t('nfc.status.disconnected'),
    connecting: t('nfc.status.connecting'),
    no_reader: t('nfc.status.noReader'),
    ready: t('nfc.status.ready'),
    busy: t('nfc.status.busy'),
    reader_busy: t('nfc.status.readerBusy', 'NFC reader in use'),
    degraded: t('nfc.status.degraded', 'Partial service'),
  };
  const subMap: Record<typeof state, string | null> = {
    disconnected: t('nfc.status.disconnectedDesc'),
    connecting: null,
    no_reader: t('nfc.status.noReaderDesc'),
    ready: t('nfc.status.realtime.connected', 'Realtime connected'),
    busy: t('nfc.status.busyDesc'),
    reader_busy: t('nfc.status.readerBusyDesc', 'Another application is holding the reader — close it to resume scanning'),
    degraded: t('nfc.status.realtime.disconnected', 'Realtime disconnected'),
  };
  const label = labelMap[state];
  const sub = subMap[state];

  const isDisconnected = state === 'disconnected';
  const isConnecting = state === 'connecting';

  // Roles without POS or guest management can't use the connector — hide the
  // tile rather than show a permanently-idle control that 403s if triggered.
  if (!canUseNfc) return null;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onMouseEnter={handleUserTrigger}
            onClick={handleUserTrigger}
            disabled={isConnecting}
            aria-label={label}
            className={cn(
              'relative inline-flex h-9 w-9 items-center justify-center rounded-[10px] border transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ocean-500/40',
              cfg.tile,
              isDisconnected ? 'cursor-pointer hover:bg-sand-200' : 'cursor-default',
            )}
          >
            <Icon
              className={cn(
                'h-[18px] w-[18px]',
                cfg.iconClass,
                isConnecting && 'animate-spin',
              )}
              aria-hidden="true"
            />
            {/* Animated pulse ring (scanning state only). */}
            {cfg.pulse && (
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-[-2px] rounded-[12px] border-2 border-current opacity-40"
                style={{ animation: 'nfcPulse 1.8s infinite' }}
              />
            )}
            {/* Status dot. */}
            <span
              aria-hidden="true"
              className={cn(
                'absolute bottom-[3px] right-[3px] h-2 w-2 rounded-full border-2 border-card',
                cfg.dot,
              )}
            />
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" align="end" className="max-w-[220px]">
          <p className="text-xs font-medium">{label}</p>
          {sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
          {isDisconnected && (
            <div className="mt-1.5 flex items-center gap-1.5 rounded-md border border-border bg-muted/60 px-2 py-1.5">
              {/* Real shaka-bridge tray icon, per OS: mac template icon
                  (inverted in dark mode, like the macOS menu bar) vs the
                  coloured Windows tray icon. */}
              {os === 'windows'
                ? <img src={trayIconWinUrl} alt="" className="h-3 w-auto shrink-0" />
                : <img src={trayIconMacUrl} alt="" className="h-3 w-auto shrink-0 dark:invert" />}
              <span className="text-[11px] leading-snug text-muted-foreground">
                {t('nfc.status.trayHint', 'When Shaka Bridge is running, this icon shows in the menu bar / system tray.')}
              </span>
            </div>
          )}
          {isDisconnected && (
            <a
              href="/downloads"
              className="mt-1.5 flex items-center gap-1 text-xs text-muted-foreground hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {t('nfc.status.allDownloads', 'All downloads')}
            </a>
          )}
          {isDisconnected && (
            <a
              href={bridgeDownloadUrl(os)}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1.5 flex items-center gap-1 text-xs text-ocean-700 hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              <Download className="h-3 w-3 shrink-0" />
              {os === 'mac'
                ? t('nfc.status.downloadMac')
                : os === 'windows'
                  ? t('nfc.status.downloadWindows')
                  : t('nfc.status.downloadOther')}
            </a>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
